import React, { useState, useCallback } from "react";
import { formStyles as s, downloadBtnStyle, DownloadIcon, requiredInput } from "./formStyles";
import SignaturePad from "../components/SignaturePad";
import { displayDateFormat } from "../utils/format";



const PerformanceImprovementForm = ({ pdfUrl = "/static/pdffilters/Performance-improvement-plan.pdf" }) => {
  const [form, setForm] = useState({
    // Page 2 — Details
    memberName: "",
    memberPosition: "",
    managerName: "",
    managerPosition: "",
    dateOfPlan: "",
    periodOfPlan: "6 weeks",
    reviewDate: "",
    finalReviewDate: "",
    objective: "",
    // Page 3
    requiredOutcomes: "",
    strategies: "",
    // Page 4
    support: "",
    responsibilities: "",
    consequences: "",
    // Page 5 — Signatures + Self Assessment
    managerPrintName_p5: "",
    managerDate_p5: "",
    memberPrintName_p5: "",
    memberDate_p5: "",
    selfAssessment: "",
    memberOtherComments: "",
    // Page 6 — Member Sig + Manager Assessment
    memberPrintName_p6: "",
    memberDate_p6: "",
    managerAssessment: "",
    managerOtherComments: "",
    // Page 7 — Final Signatures
    managerPrintName_p7: "",
    memberPrintName_p7: "",
    managerDate_p7: "",
    memberDate_p7: "",
  });

  // Signature data URLs
  const [signatures, setSignatures] = useState({
    manager_p5: null,
    member_p5: null,
    member_p6: null,
    manager_p7: null,
    member_p7: null,
  });

  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const u = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSigSave = useCallback((key) => (dataUrl) => {
    setSignatures((p) => ({ ...p, [key]: dataUrl }));
  }, []);

  const handleSigClear = useCallback((key) => () => {
    setSignatures((p) => ({ ...p, [key]: null }));
  }, []);

  const isValid =
  form.memberName.trim() &&
  form.memberPosition.trim() &&
  form.managerName.trim() &&
  form.managerPosition.trim() &&
  form.dateOfPlan.trim() &&
  form.periodOfPlan.trim() &&
  form.reviewDate.trim() &&
  form.finalReviewDate.trim() &&
  form.managerPrintName_p5.trim() &&
  form.managerDate_p5.trim() &&
  form.memberPrintName_p5.trim() &&
  form.memberDate_p5.trim() &&
  form.memberPrintName_p6.trim() &&
  form.memberDate_p6.trim() &&
  form.managerPrintName_p7.trim() &&
  form.memberPrintName_p7.trim() &&
  form.managerDate_p7.trim() &&
  form.memberDate_p7.trim() &&
  signatures.manager_p5 &&
  signatures.member_p5 &&
  signatures.member_p6 &&
  signatures.manager_p7 &&
  signatures.member_p7;

  const PERIOD_OPTIONS = ["6 weeks", "4 weeks", "8 weeks", "3 months"];





const handleDownload = async () => {
  setDownloading(true);
  setError(null);
  try {
    const { PDFDocument, PDFName } = await import("pdf-lib");
    const pdfBytes = await fetch(pdfUrl).then((r) => {
      if (!r.ok) throw new Error("Could not load PDF template");
      return r.arrayBuffer();
    });
    const pdfDoc = await PDFDocument.load(pdfBytes);
    pdfDoc.setTitle(`Performance Improvement Plan - ${form.memberName}`);
    const f = pdfDoc.getForm();

    // ← Fixed: was missing closing brace
    const setText = (name, val) => {
      try { f.getTextField(name).setText(val || ""); } catch (_) {}
    };

    // ── Text fields ──
    setText("Membername", form.memberName);
    setText("Memeberposition", form.memberPosition);
    setText("Managername", form.managerName);
    setText("Managerpositon", form.managerPosition);
    setText("Plandate", displayDateFormat(form.dateOfPlan));
    setText("Periodplan", form.periodOfPlan);
    setText("Reviewearlier", displayDateFormat(form.reviewDate));
    setText("Reviewlater", displayDateFormat(form.finalReviewDate));
    setText("improvementobjective", form.objective);
    setText("Requiredoutcomes", form.requiredOutcomes);
    setText("Strategies", form.strategies);
    setText("Support", form.support);
    setText("Responsibilites", form.responsibilities);
    setText("Consequences", form.consequences);
    setText("ManagerPrintName", form.managerPrintName_p5);
    setText("Date1", displayDateFormat(form.managerDate_p5));
    setText("MemberPrintName", form.memberPrintName_p5);
    setText("Date2", displayDateFormat(form.memberDate_p5));
    setText("ImprovementObjectives1", form.selfAssessment);
    setText("OtherComments1", form.memberOtherComments);
    setText("MemberPrintName1", form.memberPrintName_p6);
    setText("ManagerSupervisorDate", displayDateFormat(form.memberDate_p6));
    setText("ImprovementObjectives2", form.managerAssessment);
    setText("OtherComments2", form.managerOtherComments);
    setText("FinalManagerPrintName", form.managerPrintName_p7);
    setText("FinalMemberPrintName", form.memberPrintName_p7);
    setText("FinalDateManager", displayDateFormat(form.managerDate_p7));
    setText("FinalDateMember", displayDateFormat(form.memberDate_p7));

    // ── Signature embedding (defined here so it has access to pdfDoc, f, PDFName) ──
    const embedSigOnPage = async (sigDataUrl, fieldName) => {
      if (!sigDataUrl) return;
      try {
        const base64 = sigDataUrl.split(",")[1];
        const sigBytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        const sigImage = await pdfDoc.embedPng(sigBytes);

        const sigField = f.getTextField(fieldName);
        const widgets = sigField.acroField.getWidgets();
        if (widgets.length === 0) return;

        const widget = widgets[0];
        const rect = widget.getRectangle();

        const pages = pdfDoc.getPages();
        let targetPage = null;

        for (const page of pages) {
          const annotsRef = page.node.get(PDFName.of("Annots"));
          if (!annotsRef) continue;
          const annots = page.node.context.lookup(annotsRef);
          if (!annots) continue;
          const arr = annots.asArray ? annots.asArray() : [];
          for (const ref of arr) {
            if (ref === widget.ref) { targetPage = page; break; }
          }
          if (targetPage) break;
        }

        if (!targetPage) {
          for (const page of pages) {
            const annotsRef = page.node.get(PDFName.of("Annots"));
            if (!annotsRef) continue;
            const annots = page.node.context.lookup(annotsRef);
            if (!annots) continue;
            const arr = annots.asArray ? annots.asArray() : [];
            for (const ref of arr) {
              const resolved = page.node.context.lookup(ref);
              if (resolved === widget.dict) { targetPage = page; break; }
            }
            if (targetPage) break;
          }
        }

        if (!targetPage) targetPage = pages[pages.length - 1];

        f.removeField(sigField);

        const aspectRatio = sigImage.width / sigImage.height;
        let drawWidth = rect.width;
        let drawHeight = drawWidth / aspectRatio;
        if (drawHeight > rect.height) {
          drawHeight = rect.height;
          drawWidth = drawHeight * aspectRatio;
        }

        const x = rect.x + (rect.width - drawWidth) / 2;
        const y = rect.y + (rect.height - drawHeight) / 2;

        targetPage.drawImage(sigImage, { x, y, width: drawWidth, height: drawHeight });
      } catch (e) {
        console.error(`Sig embed failed for ${fieldName}:`, e);
        try { f.getTextField(fieldName).setText("[Signed]"); } catch (_) {}
      }
    };

    // ── Call signature embedding ──
    await embedSigOnPage(signatures.manager_p5, "ManagerSign");
    await embedSigOnPage(signatures.member_p5, "MemberSign");
    await embedSigOnPage(signatures.member_p6, "MemberSign1");
    await embedSigOnPage(signatures.manager_p7, "FinalSignManager");
    await embedSigOnPage(signatures.member_p7, "FinalSignMember");

    // ── Flatten and save ──
    f.flatten();
    const filled = await pdfDoc.save();
    const blob = new Blob([filled], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Performance-Improvement-${form.memberName || "Form"}.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error("PDF fill error:", err);
    setError(err.message || "Failed to generate PDF");
  } finally {
    setDownloading(false);
  }
};








    
      

  const handleReset = () => {
    setForm(Object.fromEntries(Object.keys(form).map((k) => [k, k === "periodOfPlan" ? "6 weeks" : ""])));
    setSignatures({ manager_p5: null, member_p5: null, member_p6: null, manager_p7: null, member_p7: null });
    setError(null);
  };

  return (
    <div style={s.wrapper}>
      <style>{s.spinKeyframes}</style>
      <h2 style={s.title}>Performance Improvement Plan</h2>
      <p style={s.subtitle}>Complete all sections and download the filled PDF</p>

      {/* ── Page 2: Details ── */}
      <div style={s.section}>
        <p style={s.pageLabel}>Section 1 — Plan Details</p>
        <div style={s.sectionLabel}>Team Member & Manager</div>
        <div style={s.fieldGrid2}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Team Member Name *</label>
            <input style={requiredInput(form.memberName)} value={form.memberName} onChange={(e) => u("memberName", e.target.value)} placeholder="Employee name" />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Team Member Position</label>
            <input style={requiredInput(form.memberPosition)} value={form.memberPosition} onChange={(e) => u("memberPosition", e.target.value)} placeholder="Position" />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Manager/Supervisor Name *</label>
            <input style={requiredInput(form.managerName)} value={form.managerName} onChange={(e) => u("managerName", e.target.value)} placeholder="Manager name" />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Manager Position</label>
            <input style={requiredInput(form.managerPosition)} value={form.managerPosition} onChange={(e) => u("managerPosition", e.target.value)} placeholder="Position" />
          </div>
        </div>

        <div style={{ ...s.sectionLabel, marginTop: 20 }}>Timeline</div>
        <div style={s.fieldGrid2}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Date of Plan *</label>
            <input style={requiredInput(form.dateOfPlan)} type="date" value={form.dateOfPlan} onChange={(e) => u("dateOfPlan", e.target.value)} />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Period of Plan</label>
            <select
              style={s.input}
              value={form.periodOfPlan}
              onChange={(e) => u("periodOfPlan", e.target.value)}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt} value={opt} style={s.optionStyle}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Review Date (no earlier than 2 weeks)</label>
            <input style={requiredInput(form.reviewDate)} type="date" value={form.reviewDate} onChange={(e) => u("reviewDate", e.target.value)} />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Final Review (no later than 6 weeks)</label>
            <input style={requiredInput(form.finalReviewDate)} type="date" value={form.finalReviewDate} onChange={(e) => u("finalReviewDate", e.target.value)} />
          </div>
        </div>
      </div>

      <hr style={s.divider} />

      {/* ── Pages 2-3: Objective, Outcomes, Strategies ── */}
      <div style={s.section}>
        <p style={s.pageLabel}>Section 2 — Objectives & Outcomes</p>
        <div style={s.fieldFull}>
          <div style={s.sectionLabel}>Performance Improvement Objective</div>
          <textarea style={s.textareaLarge} value={form.objective} onChange={(e) => u("objective", e.target.value)} placeholder="Describe the specific area where performance needs to improve..." />
        </div>
        <div style={s.fieldFull}>
          <div style={s.sectionLabel}>Required Outcomes</div>
          <textarea style={s.textareaLarge} value={form.requiredOutcomes} onChange={(e) => u("requiredOutcomes", e.target.value)} placeholder="Specific, measurable and realistic outcomes..." />
        </div>
        <div style={s.fieldFull}>
          <div style={s.sectionLabel}>Strategies</div>
          <textarea style={s.textareaLarge} value={form.strategies} onChange={(e) => u("strategies", e.target.value)} placeholder="How the team member will meet the required outcomes..." />
        </div>
      </div>

      <hr style={s.divider} />

      {/* ── Page 4: Support, Responsibilities, Consequences ── */}
      <div style={s.section}>
        <p style={s.pageLabel}>Section 3 — Support & Consequences</p>
        <div style={s.fieldFull}>
          <div style={s.sectionLabel}>Support</div>
          <textarea style={s.textareaLarge} value={form.support} onChange={(e) => u("support", e.target.value)} placeholder="What support will be provided..." />
        </div>
        <div style={s.fieldFull}>
          <div style={s.sectionLabel}>Responsibilities</div>
          <textarea style={s.textareaLarge} value={form.responsibilities} onChange={(e) => u("responsibilities", e.target.value)} placeholder="Responsibilities of team member and manager..." />
        </div>
        <div style={s.fieldFull}>
          <div style={s.sectionLabel}>Consequences</div>
          <textarea style={s.textarea} value={form.consequences} onChange={(e) => u("consequences", e.target.value)} placeholder="Consequences if outcomes are not met..." />
        </div>
      </div>

      <hr style={s.divider} />

      {/* ── Page 5: Initial Signatures + Self Assessment ── */}
      <div style={s.section}>
        <p style={s.pageLabel}>Section 4 — Initial Signatures & Self Assessment</p>
        <div style={s.sectionLabel}>Signatures (Page 5)</div>
        <div style={s.signatureGrid}>
          <div style={s.signatureBlock}>
            <span style={{
                ...s.signatureLabel,
                color: signatures.manager_p5 ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.7)"
              }}>
                Manager Signature *
              </span>
            <SignaturePad label="" width={300} height={120} onSave={handleSigSave("manager_p5")} onClear={handleSigClear("manager_p5")} />
            <div style={{ ...s.fieldGrid2, marginTop: 8 }}>
              <div style={s.fieldGroup}>
                <label style={s.label}>Print Name</label>
                <input style={requiredInput(form.managerPrintName_p5)} value={form.managerPrintName_p5} onChange={(e) => u("managerPrintName_p5", e.target.value)} placeholder="Manager name" />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>Date</label>
                <input style={requiredInput(form.managerDate_p5)} type="date" value={form.managerDate_p5} onChange={(e) => u("managerDate_p5", e.target.value)} />
              </div>
            </div>
          </div>
          <div style={s.signatureBlock}>
            <span style={{
                ...s.signatureLabel,
                color: signatures.member_p5 ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.7)"
              }}>
                Team Member Signature *
              </span>
            <SignaturePad label="" width={300} height={120} onSave={handleSigSave("member_p5")} onClear={handleSigClear("member_p5")} />
            <div style={{ ...s.fieldGrid2, marginTop: 8 }}>
              <div style={s.fieldGroup}>
                <label style={s.label}>Print Name</label>
                <input style={requiredInput(form.memberPrintName_p5)} value={form.memberPrintName_p5} onChange={(e) => u("memberPrintName_p5", e.target.value)} placeholder="Member name" />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>Date</label>
                <input style={requiredInput(form.memberDate_p5)} type="date" value={form.memberDate_p5} onChange={(e) => u("memberDate_p5", e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...s.sectionLabel, marginTop: 24 }}>Team Member Self-Assessment</div>
        <div style={s.fieldFull}>
          <label style={s.label}>How do you think you have performed against each objective?</label>
          <textarea style={s.textareaLarge} value={form.selfAssessment} onChange={(e) => u("selfAssessment", e.target.value)} placeholder="Self assessment..." />
        </div>
        <div style={s.fieldFull}>
          <label style={s.label}>Other Comments</label>
          <textarea style={s.textarea} value={form.memberOtherComments} onChange={(e) => u("memberOtherComments", e.target.value)} placeholder="Additional comments..." />
        </div>
      </div>

      <hr style={s.divider} />

      {/* ── Page 6: Manager Assessment ── */}
      <div style={s.section}>
        <p style={s.pageLabel}>Section 5 — Manager Assessment</p>
        <div style={s.signatureGrid}>
        <div style={s.signatureBlock}>
            <span style={{
                ...s.signatureLabel,
                color: signatures.member_p6 ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.7)"
              }}>
                Member Signature *
              </span>
            <SignaturePad label="" width={300} height={120} onSave={handleSigSave("member_p6")} onClear={handleSigClear("member_p6")} />
            </div>
        </div>
        <div style={s.fieldGrid2}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Team Member Name *</label>
            <input style={requiredInput(form.memberPrintName_p6)} value={form.memberPrintName_p6} onChange={(e) => u("memberPrintName_p6", e.target.value)} placeholder="Member name" />
          </div>
          <div style={s.fieldGroup}>
          <label style={s.label}>Team Member Date</label>
          <input style={requiredInput(form.memberDate_p6)} type="date" value={form.memberDate_p6} onChange={(e) => u("memberDate_p6", e.target.value)} />
        </div>
      </div>
        <div style={s.fieldFull}>
          <div style={s.sectionLabel}>Manager/Supervisor Assessment</div>
          <textarea style={s.textareaLarge} value={form.managerAssessment} onChange={(e) => u("managerAssessment", e.target.value)} placeholder="How do you think the employee performed..." />
        </div>
        <div style={s.fieldFull}>
          <label style={s.label}>Other Comments (Manager)</label>
          <textarea style={s.textarea} value={form.managerOtherComments} onChange={(e) => u("managerOtherComments", e.target.value)} placeholder="Additional comments..." />
        </div>
      
    </div>
    
      <hr style={s.divider} />

      {/* ── Page 7: Final Signatures ── */}
      <div style={s.section}>
        <p style={s.pageLabel}>Section 6 — Final Signatures</p>
        <div style={s.signatureGrid}>
          <div style={s.signatureBlock}>
            <span style={{
                ...s.signatureLabel,
                color: signatures.manager_p7 ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.7)"
              }}>
                Manager Signature (final) *
              </span>
            <SignaturePad label="" width={300} height={120} onSave={handleSigSave("manager_p7")} onClear={handleSigClear("manager_p7")} />
            <div style={{ ...s.fieldGrid2, marginTop: 8 }}>
              <div style={s.fieldGroup}>
                <label style={s.label}>Print Name</label>
                <input style={requiredInput(form.managerPrintName_p7)} value={form.managerPrintName_p7} onChange={(e) => u("managerPrintName_p7", e.target.value)} placeholder="Manager name" />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>Date</label>
                <input style={requiredInput(form.managerDate_p7)} type="date" value={form.managerDate_p7} onChange={(e) => u("managerDate_p7", e.target.value)} />
              </div>
            </div>
          </div>
          <div style={s.signatureBlock}>
            <span style={{
                ...s.signatureLabel,
                color: signatures.member_p7 ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.7)"
              }}>
                Team Member Signature (final) *
              </span>
            <SignaturePad label="" width={300} height={120} onSave={handleSigSave("member_p7")} onClear={handleSigClear("member_p7")} />
            <div style={{ ...s.fieldGrid2, marginTop: 8 }}>
              <div style={s.fieldGroup}>
                <label style={s.label}>Print Name</label>
                <input style={requiredInput(form.memberPrintName_p7)} value={form.memberPrintName_p7} onChange={(e) => u("memberPrintName_p7", e.target.value)} placeholder="Member name" />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>Date</label>
                <input style={requiredInput(form.memberDate_p7)} type="date" value={form.memberDate_p7} onChange={(e) => u("memberDate_p7", e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={s.btnRow}>
        <button style={downloadBtnStyle(isValid)} onClick={handleDownload} disabled={!isValid || downloading}>
          {downloading ? (<><span style={s.spinner} /> Generating PDF...</>) : (<><DownloadIcon /> Download Filled PDF</>)}
        </button>
        <button style={s.resetBtn} onClick={handleReset}>Reset</button>
      </div>
      {error && <div style={s.error}>{error}</div>}
    </div>
  );
};

export default PerformanceImprovementForm;